// Landing-page warming stripes: one stripe per year since 1951, colored by the
// contiguous U.S. mean daily high against 1951–80 (Stage 10 national_years).
// Renders nothing until the data exists (e.g. the station data source).
import { useEffect, useMemo, useState } from "react";
import * as d3 from "d3";
import API_BASE from "../api";
import { formatAnomaly } from "./mapScale";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October",
  "November", "December"];

function yearLabel(year) {
  if (!year.partial) return String(year.year);
  const last = Number(year.last_month.slice(5, 7));
  return `${year.year} (January–${MONTHS[last - 1]} so far)`;
}

// Kept for the session, like the map, so returning to the landing page is instant.
let stripesCache = null;

export default function WarmingStripes() {
  const [data, setData] = useState(stripesCache);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (stripesCache) return undefined;
    let live = true;
    fetch(`${API_BASE}/national-stripes`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (json?.years?.length) stripesCache = json;
        if (live) setData(stripesCache);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  // Symmetric scale fitted to the data, so the stripes use the full color range
  // (yearly means vary by a few degrees, far less than the daily map's ±15 °F).
  const color = useMemo(() => {
    const max = d3.max(data?.years ?? [], (y) => Math.abs(y.anomaly_f)) || 1;
    return d3.scaleDiverging(d3.interpolateRdBu).domain([max, 0, -max]);
  }, [data]);

  if (!data) return null;
  const { years, baseline_start: start, baseline_end: end } = data;
  const complete = years.filter((y) => !y.partial);
  const warmest = d3.greatest(complete, (y) => y.anomaly_f);
  const current = years[years.length - 1];
  const first = years[0].year;
  const last = current.year;
  const ticks = [first, ...[1980, 2000].filter((y) => y > first + 5 && y < last - 5), last];
  const shown = hovered ?? current;

  return (
    <section className="landing-stripes">
      <div className="landing-section-header">
        <p className="landing-eyebrow">THE LONG VIEW</p>
        <h2 className="landing-section-title">Every year since {first}, one stripe</h2>
        <p className="landing-section-sub">
          Average daily high across the continental U.S., each year compared with {start}–{end}. Blue years were
          cooler; red years were hotter.
        </p>
      </div>
      <div
        className="stripes"
        role="img"
        aria-label={`Warming stripes for ${first} to ${last}. Warmest full year: ${warmest?.year}, ${formatAnomaly(warmest?.anomaly_f ?? 0)} versus ${start}–${end}.`}
        onMouseLeave={() => setHovered(null)}
      >
        {years.map((y) => (
          <div
            key={y.year}
            className={`stripe${y.partial ? " stripe--partial" : ""}${hovered?.year === y.year ? " stripe--active" : ""}`}
            style={{ backgroundColor: color(y.anomaly_f) }}
            onMouseEnter={() => setHovered(y)}
          />
        ))}
      </div>
      <div className="stripes-axis" aria-hidden="true">
        {ticks.map((t) => (
          <span key={t} style={{ left: `${((t - first + 0.5) / years.length) * 100}%` }}>
            {t}
          </span>
        ))}
      </div>
      <p className="stripes-readout">
        <strong>{yearLabel(shown)}:</strong>{" "}
        <span className={shown.anomaly_f > 0 ? "legend-hot" : "legend-cool"}>{formatAnomaly(shown.anomaly_f)}</span>{" "}
        versus the {start}–{end} average
        {!hovered && warmest && (
          <>
            {" · "}Warmest full year: <strong>{warmest.year}</strong> ({formatAnomaly(warmest.anomaly_f)})
          </>
        )}
      </p>
    </section>
  );
}
